/* biome-ignore-all lint/style/noParameterAssign: Need to pass ref for forwardRef */
/* biome-ignore-all lint/style/useComponentExportOnlyModules: Can't wrap certain elements */
/* biome-ignore-all lint/performance/noNamespaceImport: Cleaner when working with libraries */
'use client';

import * as Select from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import React from 'react';
import { cn } from '@/app_lib/utils/utils';

const Root = Select.Root;
Root.displayName = 'SelectRoot';

const Group = Select.Group;
Group.displayName = 'SelectGroup';

const Value = Select.Value;
Value.displayName = 'SelectValue';

const Trigger = React.forwardRef<
  React.ComponentRef<typeof Select.Trigger>,
  React.ComponentPropsWithoutRef<typeof Select.Trigger>
>(({ className, children, ...rest }, ref) => (
  <Select.Trigger
    className={cn(
      'flex w-full items-center justify-between whitespace-nowrap border border-input bg-transparent px-3 py-2 typography-sm ring-accent-primary placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-accent-primary disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      className,
    )}
    ref={ref}
    {...rest}
  >
    {children}
    <Select.Icon asChild>
      <ChevronDownIcon className='h-4 w-4 opacity-50' />
    </Select.Icon>
  </Select.Trigger>
));
Trigger.displayName = Select.Trigger.displayName;

const ScrollUpButton = React.forwardRef<
  React.ComponentRef<typeof Select.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof Select.ScrollUpButton>
>(({ className, ...rest }, ref) => (
  <Select.ScrollUpButton
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    ref={ref}
    {...rest}
  >
    <ChevronUpIcon className='h-4 w-4' />
  </Select.ScrollUpButton>
));
ScrollUpButton.displayName = Select.ScrollUpButton.displayName;

const ScrollDownButton = React.forwardRef<
  React.ComponentRef<typeof Select.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof Select.ScrollDownButton>
>(({ className, ...rest }, ref) => (
  <Select.ScrollDownButton
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    ref={ref}
    {...rest}
  >
    <ChevronDownIcon className='h-4 w-4' />
  </Select.ScrollDownButton>
));
ScrollDownButton.displayName = Select.ScrollDownButton.displayName;

const Content = React.forwardRef<
  React.ComponentRef<typeof Select.Content>,
  React.ComponentPropsWithoutRef<typeof Select.Content>
>(({ className, children, position = 'popper', ...rest }, ref) => (
  <Select.Portal>
    <Select.Content
      className={cn(
        'relative z-50 max-h-96 w-[calc(100%-2px)] border-x border-y overflow-hidden bg-pure text-helper data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[side=bottom]:border-t-0 data-[side=left]:border-r-0 data-[side=right]:border-l-0 data-[side=top]:border-b-0',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-0 data-[side=left]:-translate-x-0 data-[side=right]:translate-x-0 data-[side=top]:-translate-y-0',
        className,
      )}
      position={position}
      ref={ref}
      {...rest}
    >
      <ScrollUpButton />
      <Select.Viewport
        className={cn(
          position === 'popper' && 'h-[var(--radix-select-trigger-height)] min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </Select.Viewport>
      <ScrollDownButton />
    </Select.Content>
  </Select.Portal>
));
Content.displayName = Select.Content.displayName;

const Label = React.forwardRef<
  React.ComponentRef<typeof Select.Label>,
  React.ComponentPropsWithoutRef<typeof Select.Label>
>(({ className, ...rest }, ref) => (
  <Select.Label
    className={cn('px-2 py-1.5 text-sm font-semibold font-graphik-condensed', className)}
    ref={ref}
    {...rest}
  />
));
Label.displayName = Select.Label.displayName;

const Item = React.forwardRef<
  React.ComponentRef<typeof Select.Item>,
  React.ComponentPropsWithoutRef<typeof Select.Item>
>(({ className, children, ...rest }, ref) => (
  <Select.Item
    className={cn(
      'relative flex w-full cursor-default select-none items-center py-2 pl-2 pr-8 typography-sm outline-hidden focus:bg-br focus:text-accent-primary data-disabled:pointer-events-none data-disabled:opacity-50',
      className,
    )}
    ref={ref}
    {...rest}
  >
    <span className='absolute right-2 flex h-3.5 w-3.5 items-center justify-center'>
      <Select.ItemIndicator>
        <CheckIcon className='h-4 w-4' />
      </Select.ItemIndicator>
    </span>
    <Select.ItemText>{children}</Select.ItemText>
  </Select.Item>
));
Item.displayName = Select.Item.displayName;

const Separator = React.forwardRef<
  React.ComponentRef<typeof Select.Separator>,
  React.ComponentPropsWithoutRef<typeof Select.Separator>
>(({ className, ...rest }, ref) => (
  <Select.Separator
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    ref={ref}
    {...rest}
  />
));
Separator.displayName = Select.Separator.displayName;

export { Root, Group, Value, Trigger, Content, Label, Item, Separator, ScrollUpButton, ScrollDownButton };
